import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { ProductCard } from '../components/ProductCard';
import { makeCacheKey, readCache, writeCache } from '../lib/productCache';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '')}/api`
  : '/api';

const categories = [
  { name: 'Belts', slug: 'belts', description: 'Handcrafted leather belts for every occasion' },
  { name: 'Wallets', slug: 'wallets', description: 'Premium leather wallets and cardholders' },
  { name: 'Purses', slug: 'purses', description: 'Elegant leather purses and handbags' },
  { name: 'Dog Collars', slug: 'dog_collars', description: 'Luxury collars for your furry friends' },
  { name: 'Cologne', slug: 'cologne', description: 'Sophisticated fragrances for the modern man' },
];

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backendConnected, setBackendConnected] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      const params = { featured: true, limit: 6 };
      const cacheKey = makeCacheKey('products', params);
      const cached = readCache(cacheKey);
      if (cached) {
        setFeaturedProducts(Array.isArray(cached) ? cached : []);
        setLoading(false);
      }

      try {
        const response = await axios.get(`${API}/products`, {
          params,
          timeout: 15000
        });
        setFeaturedProducts(Array.isArray(response.data) ? response.data : []);
        writeCache(cacheKey, Array.isArray(response.data) ? response.data : []);
        setBackendConnected(true);
      } catch (error) {
        console.error('Error fetching products:', error);
        if (!cached) {
          setFeaturedProducts([]);
          setBackendConnected(false);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  return (
    <div className="grain-overlay" data-testid="home-page">
      {/* ...removed static custom order notice... */}
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1664286074240-d7059e004dff?w=1920&q=80"
            alt="Premium leather belt craftsmanship"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-cream-50 via-cream-50/90 to-transparent"></div>
        </div>
        <div className="relative z-10 px-6 md:px-12 lg:px-24 max-w-3xl">
          <p className="text-xs tracking-[0.3em] uppercase text-mustard-500 mb-4 animate-fade-in">
            Premium Leather Goods
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mb-2 animate-fade-in animation-delay-100">
            <span className="text-burgundy-500">Leather</span> <span className="text-mustard-500">by</span> <span className="text-forest-600">Lindsay</span>
          </h2>
          <h1 className="font-heading text-5xl md:text-7xl font-medium tracking-tight leading-tight text-leather-900 mb-6 animate-fade-in animation-delay-100">
            Crafted for<br />Timeless Style
          </h1>
          <p className="text-lg md:text-xl text-leather-600 leading-relaxed mb-10 animate-fade-in animation-delay-200 max-w-lg">
            Discover our collection of premium leather belts, wallets, and accessories. Each piece tells a story of artisanal excellence.
          </p>
          <div className="flex flex-wrap gap-4 animate-fade-in animation-delay-300">
            <Link to="/shop/belts" className="bg-forest-500 text-white px-8 py-4 text-sm tracking-widest uppercase transition-colors duration-300 rounded-sm hover:bg-forest-600 flex items-center gap-2" data-testid="shop-now-btn">
              Shop Now <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/about" className="bg-transparent border border-burgundy-500 text-burgundy-500 px-8 py-4 text-sm tracking-widest uppercase transition-colors duration-300 rounded-sm hover:bg-burgundy-500 hover:text-white" data-testid="our-story-btn">
              Our Story
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32" data-testid="categories-section">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.3em] uppercase text-mustard-500 mb-4">Explore</p>
          <h2 className="font-heading text-4xl md:text-5xl text-leather-900">Our Collections</h2>
          <div className="flex justify-center gap-2 mt-4">
            <span className="w-8 h-1 bg-burgundy-500"></span>
            <span className="w-8 h-1 bg-mustard-500"></span>
            <span className="w-8 h-1 bg-forest-500"></span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {categories.slice(0, 3).map((cat, index) => (
            <Link
              key={cat.slug}
              to={`/shop/${cat.slug}`}
              className="group relative aspect-[4/5] overflow-hidden bg-cream-100"
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`category-${cat.slug}`}
            >
              <img
                src={cat.slug === 'belts'
                  ? 'https://images.unsplash.com/photo-1664286074240-d7059e004dff?w=600&q=80'
                  : cat.slug === 'wallets'
                  ? 'https://images.unsplash.com/photo-1620109176813-e91290f6c795?w=600&q=80'
                  : '/images/purses/Brown%20Leather%20Purse.jpg'
                }
                alt={cat.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-leather-900/80 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <h3 className="font-heading text-2xl md:text-3xl text-white mb-2">{cat.name}</h3>
                <p className="text-cream-100/80 text-sm mb-4">{cat.description}</p>
                <span className="inline-flex items-center gap-2 text-gold-500 text-sm tracking-widest uppercase group-hover:gap-4 transition-all">
                  Explore <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-6 md:mt-8">
          {categories.slice(3).map((cat, index) => (
            <Link
              key={cat.slug}
              to={`/shop/${cat.slug}`}
              className="group relative aspect-[16/9] overflow-hidden bg-cream-100"
              data-testid={`category-${cat.slug}`}
            >
              <img
                src={cat.slug === 'cologne'
                  ? 'https://images.unsplash.com/photo-1594036049305-007afaf640a4?w=800&q=80'
                  : '/images/collars/dog-collar.jpg'
                }
                alt={cat.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-leather-900/80 via-leather-900/50 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-6 md:p-8">
                <h3 className="font-heading text-2xl md:text-3xl text-white mb-2">{cat.name}</h3>
                <p className="text-cream-100/80 text-sm mb-4">{cat.description}</p>
                <span className="inline-flex items-center gap-2 text-gold-500 text-sm tracking-widest uppercase group-hover:gap-4 transition-all">
                  Explore <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="px-6 md:px-12 lg:px-24 py-24 bg-cream-100" data-testid="featured-section">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-16">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-forest-500 mb-4">Curated Selection</p>
            <h2 className="font-heading text-4xl md:text-5xl text-leather-900">Featured Products</h2>
          </div>
          <Link to="/shop/belts" className="border border-mustard-500 text-mustard-600 px-8 py-4 text-sm tracking-widest uppercase transition-colors duration-300 rounded-sm hover:bg-mustard-500 hover:text-white">
            View All
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="spinner"></div>
          </div>
        ) : featuredProducts && Array.isArray(featuredProducts) && featuredProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-leather-600">Featured products coming soon</p>
          </div>
        )}
      </section>

      {/* Story Section */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-screen" data-testid="story-section">
        <div className="relative">
          <img
            src="https://images.unsplash.com/photo-1620109176813-e91290f6c795?w=800&q=80"
            alt="Leather craftsmanship"
            className="w-full h-full object-cover min-h-[50vh] lg:min-h-screen"
          />
          <div className="absolute top-8 left-8 w-16 h-16 border-2 border-mustard-500 opacity-60"></div>
          <div className="absolute bottom-8 right-8 w-24 h-24 border-2 border-forest-500 opacity-40"></div>
        </div>
        <div className="flex items-center px-6 md:px-12 lg:px-16 py-16 lg:py-24 bg-cream-50">
          <div className="max-w-lg">
            <p className="text-xs tracking-[0.3em] uppercase text-burgundy-500 mb-4">Our Story</p>
            <h2 className="font-heading text-4xl md:text-5xl text-leather-900 mb-8">
              The Art of<br />Leather Craft
            </h2>
            <p className="text-leather-600 leading-relaxed mb-6">
              At Belt & Tingz, we believe in the timeless appeal of quality leather goods. Each piece in our collection is handcrafted by skilled artisans who have honed their craft over generations.
            </p>
            <p className="text-leather-600 leading-relaxed mb-8">
              From selecting the finest full-grain leather to the final stitch, every detail is considered. The result is a product that not only looks exceptional but improves with age, developing a unique patina that tells your story.
            </p>
            <Link to="/about" className="btn-secondary">
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* Quality Promise */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-leather-900 text-cream-100" data-testid="quality-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-mustard-400 mb-4">Our Promise</p>
          <h2 className="font-heading text-4xl md:text-5xl mb-12">Quality You Can Trust</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <div className="w-16 h-16 border border-burgundy-400 flex items-center justify-center mx-auto mb-6">
                <span className="font-accent text-2xl text-burgundy-400">01</span>
              </div>
              <h3 className="font-heading text-xl mb-3">Premium Materials</h3>
              <p className="text-cream-100/70 text-sm">
                Only the finest full-grain leather sourced from trusted tanneries around the world.
              </p>
            </div>
            <div>
              <div className="w-16 h-16 border border-mustard-400 flex items-center justify-center mx-auto mb-6">
                <span className="font-accent text-2xl text-mustard-400">02</span>
              </div>
              <h3 className="font-heading text-xl mb-3">Expert Craftsmanship</h3>
              <p className="text-cream-100/70 text-sm">
                Each piece is handcrafted by skilled artisans with decades of experience.
              </p>
            </div>
            <div>
              <div className="w-16 h-16 border border-forest-400 flex items-center justify-center mx-auto mb-6">
                <span className="font-accent text-2xl text-forest-400">03</span>
              </div>
              <h3 className="font-heading text-xl mb-3">Lifetime Guarantee</h3>
              <p className="text-cream-100/70 text-sm">
                We stand behind every product with our comprehensive lifetime warranty.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
